import { useState } from "react";
import type { MealItem, MealType, MealTotals } from "../api/diary";
import MealIcon from "./MealIcon";
import MealItemRow from "./MealItemRow";

export type MealCardTheme = {
  fg: string;
  hint: string;
  cardBg: string;
  divider: string;
  accent: string;
  addBtn: string;
};

export type MealCardProps = {
  meal: MealType;
  title: string;
  totals: MealTotals;
  items: MealItem[];
  onAdd: () => void;
  onItemTap: (it: MealItem) => void;
  theme: MealCardTheme;
  kcalGoal?: number;
};

function fmt(n: number, digits = 2): string {
  if (!Number.isFinite(n)) return "0";
  return n.toFixed(digits).replace(".", ",");
}

export default function MealCard({
  meal,
  title,
  totals,
  items,
  onAdd,
  onItemTap,
  theme,
  kcalGoal = 2000,
}: MealCardProps) {
  const [expanded, setExpanded] = useState(true);
  const rsk = Math.round((totals.kcal / kcalGoal) * 100);
  const toggle = () => setExpanded((v) => !v);

  return (
    <div style={{ margin: "6px 8px 0 8px", background: theme.cardBg, borderRadius: 10, overflow: "hidden", boxSizing: "border-box" }}>
      {/* Шапка карточки */}
      <div style={{ display: "flex", alignItems: "center", padding: "10px 12px", gap: 9, minWidth: 0 }}>
        <button
          type="button"
          onClick={toggle}
          aria-label={expanded ? "Свернуть продукты" : "Развернуть продукты"}
          aria-expanded={expanded}
          style={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            alignItems: "center",
            gap: 9,
            background: "transparent",
            border: "none",
            padding: 0,
            margin: 0,
            cursor: "pointer",
            color: theme.fg,
            font: "inherit",
            textAlign: "left",
            boxSizing: "border-box",
            WebkitTapHighlightColor: "transparent",
            WebkitAppearance: "none",
            appearance: "none",
          }}
        >
          <MealIcon meal={meal} size={24} />
          <span style={{ fontSize: 15, fontWeight: 700, flex: 1, minWidth: 0, color: theme.fg, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{title}</span>
          <span style={{ textAlign: "right", lineHeight: 1.1 }}>
            <span style={{ display: "block", fontSize: 18, fontWeight: 700, color: theme.fg, fontVariantNumeric: "tabular-nums" }}>
              {Math.round(totals.kcal)}
            </span>
            <span style={{ display: "block", fontSize: 10, color: theme.hint, marginTop: 2 }}>Калории</span>
          </span>
          <span
            aria-hidden
            style={{
              color: theme.hint,
              fontSize: 16,
              lineHeight: 1,
              marginLeft: 4,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 14,
              transform: expanded ? "rotate(0deg)" : "rotate(-90deg)",
              transition: "transform 200ms ease",
            }}
          >
            ⌄
          </span>
        </button>
        <button
          type="button"
          onClick={onAdd}
          aria-label="Добавить продукт"
          style={{
            width: 22,
            height: 22,
            padding: 6,
            margin: 0,
            boxSizing: "content-box",
            background: "transparent",
            color: theme.addBtn,
            border: "none",
            font: "inherit",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            WebkitTapHighlightColor: "transparent",
            WebkitAppearance: "none",
            appearance: "none",
          }}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
        </button>
      </div>

      {/* Разделитель */}
      <div style={{ height: 1, background: theme.divider, margin: "0 12px" }} />

      {/* Полоса 4 чисел — всегда видна */}
      <div style={{ display: "flex", alignItems: "center", padding: "8px 12px", color: theme.hint }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", flex: 1, minWidth: 0, gap: 6 }}>
          <span style={{ fontSize: 11, color: theme.hint, textAlign: "center", fontVariantNumeric: "tabular-nums" }}>{fmt(totals.fat)}</span>
          <span style={{ fontSize: 11, color: theme.hint, textAlign: "center", fontVariantNumeric: "tabular-nums" }}>{fmt(totals.carbs)}</span>
          <span style={{ fontSize: 11, color: theme.hint, textAlign: "center", fontVariantNumeric: "tabular-nums" }}>{fmt(totals.protein)}</span>
          <span style={{ fontSize: 11, color: theme.hint, textAlign: "center", fontVariantNumeric: "tabular-nums" }}>{rsk}%</span>
        </div>
      </div>

      {/* Список продуктов — сворачивается */}
      {items.length > 0 && (
        <Collapse open={expanded}>
          <div style={{ height: 1, background: theme.divider, margin: "0 12px" }} />
          <div>
            {items.map((it, idx) => (
              <MealItemRow
                key={it.id}
                item={it}
                onTap={() => onItemTap(it)}
                fg={theme.fg}
                hint={theme.hint}
                accent={theme.accent}
                divider={theme.divider}
                isLast={idx === items.length - 1}
                kcalGoal={kcalGoal}
              />
            ))}
          </div>
        </Collapse>
      )}
    </div>
  );
}

function Collapse({ open, children }: { open: boolean; children: React.ReactNode }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateRows: open ? "1fr" : "0fr",
        gridTemplateColumns: "minmax(0, 1fr)",
        transition: "grid-template-rows 200ms ease",
        minWidth: 0,
      }}
    >
      <div style={{ overflow: "hidden", minWidth: 0 }}>{children}</div>
    </div>
  );
}
